import { usePaginatedList } from '@/hooks/usePaginatedList';
import { api } from '@/lib/api-client';
import type { PaginationResponse } from '@/models/pagination';

import type { Snapshot, SnapshotSummary } from '../model/snapshot';

export interface GetSnapshotsParams {
  state?: Snapshot['state'];
  /** admin-api defaults to effective_at desc, with created_at desc as tie-break. */
  sort_by?: 'created_at' | 'effective_at' | 'name' | 'state';
  sort_order?: 'asc' | 'desc';
  limit: number;
  offset: number;
}

export const snapshotsQueryKey = {
  all: ['snapshots'] as const,
  // Under `all`, so the invalidation every snapshot mutation does reaches the
  // detail too and nothing has to patch it by hand.
  detail: (id: string) => [...snapshotsQueryKey.all, 'detail', id],
};

export const getSnapshots = async ({
  state,
  sort_by,
  sort_order,
  limit,
  offset,
}: GetSnapshotsParams) => {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  if (state) params.append('state', state);
  if (sort_by) params.append('sort_by', sort_by);
  if (sort_order) params.append('sort_order', sort_order);

  const response = await api.get<
    { snapshots: SnapshotSummary[] } & PaginationResponse
  >(`/v1/store/snapshots?${params.toString()}`);
  return response.data;
};

/**
 * One page of snapshots. Without `page` it follows the URL's `?page=`; `key`
 * keeps a second list of the same size from sharing its cache entry.
 */
export const useSnapshotsPage = ({
  limit,
  page,
  key = [],
  filters,
}: {
  limit: number;
  page?: number;
  key?: string[];
  filters?: Omit<GetSnapshotsParams, 'limit' | 'offset'>;
}) =>
  usePaginatedList(
    [...snapshotsQueryKey.all, ...key, ...(filters ? [filters] : [])],
    (pagination) => getSnapshots({ ...filters, ...pagination }),
    { limit, page },
  );
